import type { ChannelSlice } from '../ChannelSlice';
import { NEW_TABLE_RESET } from '../TableModel';
import type { TableConstructionProgress } from '../TableModel';
import type { WidgetHeaderElement } from '../WidgetModel';
import { COMMON_HEADER_ELEMENTS, STANDALONE_COMMON_HEADER_ELEMENTS } from '../WidgetModel/impl';
import type { WidgetTabSlice } from '../WidgetTabSlice';
import type { EventsWidgetModel, EventsWidgetViewDetails } from './index';
import { Duration } from '@js-joda/core';
import type { LocalDate } from '@js-joda/core';
import { computed, effect, signal } from '@preact/signals-react';
import type { ReadonlySignal, Signal } from '@preact/signals-react';
import { hashKey } from '@tanstack/query-core';
import type { QueryObserverOptions } from '@tanstack/query-core';
import { Slugs } from '@thinkalpha/common/constants/slugs.js';
import { notNullOrUndefined } from '@thinkalpha/common/util/notNullOrUndefined.js';
import type { ConcreteIndicator } from '@thinkalpha/platform-ws-client/contracts/dictionary.js';
import { RowIdField } from '@thinkalpha/table-client';
import type { Logger } from '@thinkalpha/ui-ioc/core/types/Logger';
import { noop } from 'es-toolkit';
import { optional } from 'inversify';
import type { TableStatus } from 'src/components/table-view/model';
import type { EventType } from 'src/components/ui/EventList';
import type { IdeaDTO } from 'src/dtos/Idea';
import { SearchPlanDTO } from 'src/dtos/Idea/SearchPlanDTO';
import { eventsQuery } from 'src/features/events-list/eventsAdapter';
import type { EventsTimeList } from 'src/features/events-list/eventsAdapter';
import { eventsGridColumns } from 'src/features/events-list/grid/eventsGridColumns';
import { createEventsList } from 'src/features/events-list/model';
import { ReactiveInjectable, reacts, inject, injectable } from 'src/features/ioc';
import { ScannerPlanDTO } from 'src/features/scanner/dtos/ScannerPlanDTO';
import { ScreenerPlanDTO } from 'src/features/screener/dtos/ScreenerPlanDTO';
import type { InstrumentType } from 'src/features/symbol-picker/state';
import { ideaDTOToStage } from 'src/features/table/lib/ideaStages';
import { TimeListColumns } from 'src/features/time-list-grid/TimeListColumns';
import { WatchlistPlanDTO } from 'src/features/watchlist/dtos/WatchlistPlanDTO';
import { connectReducerDevtools } from 'src/lib/devtools';
import type { ReducerDevtoolsConnection } from 'src/lib/devtools';
import { createTable } from 'src/lib/table';
import { FilterStage, OutputStage, SourceStage, UnionStage } from 'src/lib/tableStages';
import type { Stage } from 'src/lib/tableStages';
import type { EventsPersistedState, EventsWidgetAdapter } from 'src/models/EventsWidgetAdapter';
import { getIdeaDTOByIdQuery } from 'src/queries/ideas';
import type { WorkspaceService } from 'src/services/WorkspaceService';
import type { ByIdState } from 'src/store/reducers/tab/byId';
import type { ContainerViewModel, EventsWidgetViewModel, WidgetTabViewModel } from 'src/store/types';
import type { FetchableTableCreationResultWithColumns } from 'src/types';
import type { ReactBindings } from 'src/types/bindings';
import type { ConnectionStatusSignal } from 'src/types/EventBus';
import { EventBusBuilder, MergedEventBus } from 'src/types/EventBus/EventBus';
import type { SlowIdeaSource } from 'src/types/slowIdeaSource';
import { v4 } from 'uuid';

const DEFAULT_SYMBOL_RETENTION = Duration.ofSeconds(0);

type EventsWidgetModelAction =
    | {
          type: 'INITIALIZE_SUCCESS';
          changeCloseSlug: Signal<ConcreteIndicator>;
          symbolSlug: Signal<ConcreteIndicator>;
          universeId: ReadonlySignal<string | null>;
          symbolRetention: Duration;
      }
    | { type: 'UPDATE_FAST_IDEAS'; ideas: (IdeaDTO<ScannerPlanDTO> | IdeaDTO<SearchPlanDTO>)[] }
    | {
          type: 'UPDATE_SLOW_IDEAS';
          ideas: (IdeaDTO<ScreenerPlanDTO> | IdeaDTO<WatchlistPlanDTO> | { universeId: string })[];
      }
    | { type: 'UPDATE_SYMBOL_FILTER'; symbol: string | null }
    | { type: 'UPDATE_SYMBOL_RETENTION'; duration: Duration }
    | { type: 'SYMBOLS_SNAPSHOT_RECEIVED'; symbols: Iterable<string> }
    | { type: 'SYMBOLS_UPDATE_RECEIVED'; added: Iterable<string>; removed: Iterable<string> }
    | { type: 'RESET' };

type EventsWidgetModelState =
    | { type: 'initializing' }
    | {
          type: 'waiting-for-symbols';
          instanceId: string;
          changeCloseSlug: Signal<ConcreteIndicator>;
          symbolSlug: Signal<ConcreteIndicator>;
          universeId: ReadonlySignal<string | null>;
          fastIdeas: Signal<(IdeaDTO<ScannerPlanDTO> | IdeaDTO<SearchPlanDTO>)[]>;
          symbolFilter: Signal<string | null>;
          symbolRetention: Signal<Duration>;
      }
    | {
          type: 'ready';
          instanceId: string;
          changeCloseSlug: Signal<ConcreteIndicator>;
          symbolSlug: Signal<ConcreteIndicator>;
          universeId: ReadonlySignal<string | null>;
          fastIdeas: Signal<(IdeaDTO<ScannerPlanDTO> | IdeaDTO<SearchPlanDTO>)[]>;
          symbolFilter: Signal<string | null>;
          symbolRetention: Signal<Duration>;
          symbols: Signal<Set<string>>;
      };

export function eventsWidgetModelReducer(
    state: EventsWidgetModelState,
    action: EventsWidgetModelAction,
): EventsWidgetModelState {
    switch (action.type) {
        case 'INITIALIZE_SUCCESS':
            return {
                type: 'waiting-for-symbols',
                instanceId: v4(),
                changeCloseSlug: action.changeCloseSlug,
                symbolSlug: action.symbolSlug,
                universeId: action.universeId,
                fastIdeas: signal<(IdeaDTO<ScannerPlanDTO> | IdeaDTO<SearchPlanDTO>)[]>([]),
                symbolFilter: signal<string | null>(null),
                symbolRetention: signal<Duration>(action.symbolRetention),
            };
        case 'UPDATE_FAST_IDEAS':
            if (state.type === 'initializing') {
                return state;
            }

            const currentIdeas = state.fastIdeas.value;
            const newIdeaIds = action.ideas.map((idea) => idea.idea.id);
            const currentIdeaIds = currentIdeas.map((idea) => idea.idea.id);

            const ideasChanged =
                newIdeaIds.length !== currentIdeaIds.length || !newIdeaIds.every((id) => currentIdeaIds.includes(id));

            state.fastIdeas.value = action.ideas;

            if (ideasChanged && state.type === 'ready') {
                return {
                    type: 'waiting-for-symbols',
                    instanceId: v4(),
                    changeCloseSlug: state.changeCloseSlug,
                    symbolSlug: state.symbolSlug,
                    universeId: state.universeId,
                    fastIdeas: state.fastIdeas,
                    symbolFilter: state.symbolFilter,
                    symbolRetention: state.symbolRetention,
                };
            }

            return state;
        case 'UPDATE_SLOW_IDEAS':
            if (state.type === 'initializing') {
                return state;
            }

            const slowIdeasWithUniverse = action.ideas.filter((idea) => 'universeId' in idea);
            const newUniverse = slowIdeasWithUniverse.length > 0 ? slowIdeasWithUniverse[0].universeId : null;

            if (state.type === 'ready' && state.universeId.value !== newUniverse) {
                return {
                    type: 'waiting-for-symbols',
                    instanceId: v4(),
                    changeCloseSlug: state.changeCloseSlug,
                    symbolSlug: state.symbolSlug,
                    universeId: state.universeId,
                    fastIdeas: state.fastIdeas,
                    symbolFilter: state.symbolFilter,
                    symbolRetention: state.symbolRetention,
                };
            }

            return state;
        case 'UPDATE_SYMBOL_FILTER':
            if (state.type !== 'initializing') {
                state.symbolFilter.value = action.symbol;
            }

            return state;
        case 'UPDATE_SYMBOL_RETENTION':
            if (state.type !== 'initializing') state.symbolRetention.value = action.duration;
            return state;
        case 'SYMBOLS_SNAPSHOT_RECEIVED':
            if (state.type === 'initializing') return state;
            if (state.type === 'ready') {
                state.symbols.value = new Set(action.symbols);
                return state;
            }
            return { ...state, type: 'ready', symbols: signal(new Set(action.symbols)) };
        case 'SYMBOLS_UPDATE_RECEIVED': {
            if (state.type !== 'ready') return state;
            const symbols = new Set(state.symbols.value);
            for (const symbol of action.removed) symbols.delete(symbol);
            for (const symbol of action.added) symbols.add(symbol);
            state.symbols.value = symbols;
            return state;
        }
        case 'RESET':
            return { type: 'initializing' };
        default:
            return state;
    }
}

type SlowIdeaInput =
    | IdeaDTO<ScreenerPlanDTO>
    | IdeaDTO<WatchlistPlanDTO>
    | { universeId: string }
    | { type: string; id: string };

function isUniverseIdFormat(idea: SlowIdeaInput): idea is { universeId: string } {
    return 'universeId' in idea && !('idea' in idea);
}

function isTypeIdFormat(idea: SlowIdeaInput): idea is { type: string; id: string } {
    return 'type' in idea && !('idea' in idea) && (idea as { type: string }).type === 'universe' && 'id' in idea;
}

@injectable()
export class EventsWidgetModelImpl extends ReactiveInjectable implements EventsWidgetModel {
    readonly gridColumns: TimeListColumns;
    readonly eventsList: EventsTimeList;
    #adapter: EventsWidgetAdapter;
    #queryClient: ReactBindings['QueryClient'];
    #formulaService: ReactBindings['FormulaService'];
    #table: ReactBindings['TableModel'];
    #defaultUniverseServiceProvider: ReactBindings['DefaultUniverseServiceProvider'];
    #slugMapService: ReactBindings['SlugMapService'];
    #slugMapServiceProvider: ReactBindings['SlugMapServiceProvider'];
    #channelSlice: ChannelSlice;
    #widgetTabSlice: WidgetTabSlice;

    #allowedInstrumentTypes: ReadonlySignal<InstrumentType[]> = signal(['equity']);

    // New header system signals
    #connectionStatus: Signal<
        ConnectionStatusSignal['widget::connectionStatus'] extends ReadonlySignal<infer T> ? T : never
    > = signal({
        status: 'uninitialized' as TableStatus,
        progress: undefined,
        failureCount: 0,
    });

    /**
     * Second row of controls for the Events widget (go-to date + event type filter).
     * These elements use the widget header architecture but are styled differently
     * from standard header elements — they belong exclusively in this row.
     */
    eventsControlsHeaderElements: WidgetHeaderElement[] = [
        { type: 'overflowDropdown', position: 0, alwaysVisible: true },
        { type: 'eventsGoToDate', position: 10, priority: 1 },
        // Calendar view toggle hidden until the API is reworked. Re-enable by
        // restoring this element: { type: 'eventsCalendarToggle', position: 15, priority: 3 }
        { type: 'spacer', position: 50, alwaysVisible: true },
        { type: 'eventsEventTypeFilter', position: 60, priority: 2 },
    ];

    headerElements!: WidgetHeaderElement[];

    eventBus!: EventsWidgetModel['eventBus'];

    constructor(
        @inject('EventsWidgetAdapter') adapter: EventsWidgetAdapter,
        @inject('Store') store: ReactBindings['Store'],
        @inject('Logger') logger: Logger,
        @inject('DefaultUniverseServiceProvider')
        defaultUniverseServiceProvider: ReactBindings['DefaultUniverseServiceProvider'],
        @inject('SlugMapServiceProvider') slugMapServiceProvider: ReactBindings['SlugMapServiceProvider'],
        @inject('SlugMapService') slugMapService: ReactBindings['SlugMapService'],
        @inject('TableModel') table: ReactBindings['TableModel'],
        @inject('QueryClient') queryClient: ReactBindings['QueryClient'],
        @inject('FormulaService') formulaService: ReactBindings['FormulaService'],
        @inject('ChannelSlice') channelSlice: ChannelSlice,
        @inject('WidgetTabSlice') widgetTabSlice: WidgetTabSlice,
        @inject('WorkspaceService') @optional() workspaceService?: WorkspaceService,
        @inject('TabId') @optional() tabId?: string,
    ) {
        // eslint-disable-next-line prefer-rest-params -- forwarding constructor args
        super(...arguments);

        this.eventsList = this.disposableStack.use(createEventsList(queryClient));
        this.#adapter = adapter;
        this.gridColumns = this.disposableStack.use(new TimeListColumns(eventsGridColumns, adapter));
        this.#queryClient = queryClient;
        this.#formulaService = formulaService;
        this.#table = table;
        this.#defaultUniverseServiceProvider = defaultUniverseServiceProvider;
        this.#slugMapService = slugMapService;
        this.#slugMapServiceProvider = slugMapServiceProvider;
        this.#channelSlice = channelSlice;
        this.#widgetTabSlice = widgetTabSlice;

        this.#state = { type: 'initializing' };

        this.#logger = logger.getSubLogger({ name: 'EventsWidgetModel' });

        if (tabId && workspaceService) {
            workspaceService.registerWidget(tabId, this);
            this.disposableStack.defer(() => workspaceService.deregisterWidget(tabId));
        }
    }

    get fastIdeas(): ReadonlySignal<(IdeaDTO<ScannerPlanDTO> | IdeaDTO<SearchPlanDTO>)[]> | null {
        if (this.#state.type !== 'initializing') {
            return this.#state.fastIdeas;
        }
        return null;
    }

    async init(tabId?: string) {
        const baseElements = tabId
            ? COMMON_HEADER_ELEMENTS
            : STANDALONE_COMMON_HEADER_ELEMENTS.filter((el) => el.type !== 'channelPicker' && el.type !== 'widgetName');
        this.headerElements = [
            ...baseElements,
            { type: 'symbolPicker', position: 25, priority: 80, placeholder: 'symbol' },
            { type: 'symbolRetention', position: 28, priority: 50 },
            { type: 'helpDocument', documentId: 'events-overview', position: 30, priority: 40 },
            {
                type: 'ideasMenu',
                key: 'slow',
                position: 54,
                priority: 60,

                selectType: 'single',
                ideaType: 'slow',
                ideaPlanTypes: ['universe', 'screener', 'watchlist'],
                showSelected: false,
                allowDelete: false,
            },
            {
                type: 'ideasMenu',
                key: 'fast',
                position: 56,
                priority: 60,

                selectType: 'multi',
                ideaType: 'fast',
                ideaPlanTypes: ['scanner', 'if-then'],
                showSelected: true,
                allowDelete: false,
            },
            { type: 'connectionStatus', position: 995, priority: 995 },
        ];

        this.#devtools = connectReducerDevtools({ name: `Events [${tabId ?? 'standalone'}]` });
        if (this.#devtools) {
            this.disposableStack.use(this.#devtools);
            this.#devtools.init(this.#state);
        }

        if (tabId) {
            // Initialize slices for the new widget header system (workspace only)
            this.#channelSlice.init(tabId);
            this.#widgetTabSlice.init(tabId);
            this.disposableStack.use(this.#channelSlice);
            this.disposableStack.use(this.#widgetTabSlice);
        }

        // Build the EventBus (must be after slice init so slices provide signals in workspace mode)
        this.eventBus = this.#buildEventBus(tabId);

        // Read initial state from adapter
        const adapterState = this.#adapter.getState();

        // Hydrate gotoDate, eventTypes, and calendar state from adapter
        this.#gotoDateSignal.value = adapterState.gotoDate ?? null;
        this.#eventTypes.value = adapterState.eventTypes || [];
        this.#displayingCalendarSignal.value = adapterState.displayingCalendar ?? false;
        this.#splitPercentageSignal.value = adapterState.splitPercentage ?? 50;

        // The universeId field stores either a universe ID or a screener/watchlist idea ID.
        // On init, probe the stored value: if it resolves as an idea DTO it's a screener/watchlist;
        // otherwise treat it as a universe ID.
        const storedId = adapterState.universeId;
        if (storedId) {
            try {
                const dto = await this.#queryClient.fetchQuery(
                    getIdeaDTOByIdQuery(this.#queryClient, this.#formulaService, this.#slugMapService, storedId),
                );
                if (dto.plan instanceof ScreenerPlanDTO || dto.plan instanceof WatchlistPlanDTO) {
                    if (TRACE) {
                        this.#logger.trace('Initializing with slow idea from adapter', { ideaId: storedId });
                    }
                    this.#slowIdeaSourceSignal.value = {
                        type: 'idea',
                        id: storedId,
                        dto: dto as IdeaDTO<ScreenerPlanDTO | WatchlistPlanDTO>,
                    };
                } else {
                    if (TRACE) {
                        this.#logger.trace('Stored ID resolved to non-slow idea, treating as universe', {
                            storedId,
                            planType: dto.plan?.constructor?.name,
                        });
                    }
                    this.#slowIdeaSourceSignal.value = { type: 'universe', id: storedId };
                }
            } catch {
                if (TRACE) {
                    this.#logger.trace('Initializing with universe from adapter', { universeId: storedId });
                }
                this.#slowIdeaSourceSignal.value = { type: 'universe', id: storedId };
            }
        }

        if (this.#slowIdeaSourceSignal.value === null) {
            // No stored ID — initialize with the default universe
            const defaultUniverseService = await this.#defaultUniverseServiceProvider();
            const defaultUniverseId = defaultUniverseService.universe.value.id;
            if (TRACE) {
                this.#logger.trace('Initializing with default universe', { defaultUniverseId });
            }
            this.#adapter.persist({ universeId: defaultUniverseId });
            this.#slowIdeaSourceSignal.value = { type: 'universe', id: defaultUniverseId };
        }

        // Subscribe to adapter changes
        this.disposableStack.defer(
            this.#adapter.subscribe((adapterState: EventsPersistedState) => {
                const newStoredId = adapterState.universeId;
                const currentId = this.#slowIdeaSourceSignal.value?.id ?? null;

                if (newStoredId !== currentId) {
                    if (
                        newStoredId !== null &&
                        this.#slowIdeaSourceSignal.value?.type === 'idea' &&
                        newStoredId === currentId
                    ) {
                        // Adapter echoing back the idea ID we already set — no-op
                    } else {
                        if (TRACE) {
                            this.#logger.trace({
                                message: 'Adapter changed slow idea source',
                                universeId: newStoredId,
                            });
                        }
                        // Treat adapter-driven changes as universe selections (ideas are set in-memory first)
                        this.#slowIdeaSourceSignal.value = newStoredId ? { type: 'universe', id: newStoredId } : null;
                        if (this.#state.type !== 'initializing') {
                            this.#updateState({
                                type: 'UPDATE_SLOW_IDEAS',
                                ideas: newStoredId ? [{ universeId: newStoredId }] : [],
                            });
                        }
                    }
                }

                const currentTypes = this.#eventTypes.value;
                const newTypes = adapterState.eventTypes;
                if (
                    newTypes &&
                    (newTypes.length !== currentTypes.length || newTypes.some((t, i) => t !== currentTypes[i]))
                ) {
                    if (TRACE) {
                        this.#logger.trace({
                            message: 'Event types changed from adapter',
                            eventTypes: adapterState.eventTypes,
                        });
                    }
                    this.#eventTypes.value = adapterState.eventTypes;
                }

                if (adapterState.gotoDate !== this.#gotoDateSignal.value) {
                    if (TRACE) {
                        this.#logger.trace({
                            message: 'Go to date changed from adapter',
                            gotoDate: adapterState.gotoDate,
                        });
                    }
                    this.#gotoDateSignal.value = adapterState.gotoDate;
                }

                // Update symbolFilter in state from adapter
                if (this.#state.type !== 'initializing') {
                    const currentValue = this.#state.symbolFilter.value;
                    const newValue = adapterState.symbolFilter ?? null;

                    if (
                        currentValue !== newValue &&
                        !((currentValue === null && newValue === '') || (currentValue === '' && newValue === null))
                    ) {
                        this.#updateState({ type: 'UPDATE_SYMBOL_FILTER', symbol: adapterState.symbolFilter });
                    }
                }

                // Update symbolRetention in state from adapter
                if (this.#state.type !== 'initializing') {
                    const adapterRetention = adapterState.symbolRetention;
                    const parsedDuration = adapterRetention
                        ? Duration.parse(adapterRetention)
                        : DEFAULT_SYMBOL_RETENTION;
                    const currentDuration = this.#state.symbolRetention.value;
                    if (!parsedDuration.equals(currentDuration)) {
                        this.#updateState({ type: 'UPDATE_SYMBOL_RETENTION', duration: parsedDuration });
                    }
                }

                // Update displayingCalendar from adapter
                const newDisplayingCalendar = adapterState.displayingCalendar ?? false;
                if (newDisplayingCalendar !== this.#displayingCalendarSignal.value) {
                    this.#displayingCalendarSignal.value = newDisplayingCalendar;
                }

                // Update splitPercentage from adapter
                const newSplitPercentage = adapterState.splitPercentage ?? 50;
                if (newSplitPercentage !== this.#splitPercentageSignal.value) {
                    this.#splitPercentageSignal.value = newSplitPercentage;
                }
            }),
        );

        const slugMapService = await this.#slugMapServiceProvider();

        const symbolSlug = computed(() => slugMapService.slugMap.value[Slugs.symbol]);
        const changeCloseSlug = computed(() => slugMapService.slugMap.value[Slugs.symbolChangeClose]);

        // Hydrate symbolRetention from adapter, or use default if not set
        const adapterSymbolRetention = this.#adapter.getState().symbolRetention;
        const initialSymbolRetention = adapterSymbolRetention
            ? Duration.parse(adapterSymbolRetention)
            : DEFAULT_SYMBOL_RETENTION;

        this.#updateState({
            type: 'INITIALIZE_SUCCESS',
            changeCloseSlug,
            symbolSlug,
            universeId: this.#universeId,
            symbolRetention: initialSymbolRetention,
        });

        // Apply adapter-provided initial state (must come after INITIALIZE_SUCCESS)
        const initialSymbol = adapterState.symbolFilter;
        if (initialSymbol) {
            this.#updateState({ type: 'UPDATE_SYMBOL_FILTER', symbol: initialSymbol });
        }

        // Load fast ideas from adapter IDs
        const initialFastIdeaIds = adapterState.fastIdeasIds;
        if (initialFastIdeaIds.length > 0) {
            const ideaResults = await Promise.all(
                initialFastIdeaIds.map((id) =>
                    this.#queryClient
                        .fetchQuery(
                            getIdeaDTOByIdQuery(this.#queryClient, this.#formulaService, this.#slugMapService, id),
                        )
                        .catch(() => null),
                ),
            );
            const validIdeas = ideaResults
                .filter(notNullOrUndefined)
                .filter(
                    (idea): idea is IdeaDTO<ScannerPlanDTO> | IdeaDTO<SearchPlanDTO> =>
                        idea.plan instanceof ScannerPlanDTO || idea.plan instanceof SearchPlanDTO,
                );
            if (validIdeas.length > 0) {
                this.#updateState({ type: 'UPDATE_FAST_IDEAS', ideas: validIdeas });
            }
        }

        const queryOptions: ReadonlySignal<QueryObserverOptions<FetchableTableCreationResultWithColumns>> = computed(
            () => {
                const symbolFilterValue = this.symbolFilter?.value ?? null;
                const source = this.#slowIdeaSourceSignal.value;
                const fastIdeasValue = this.#state.type !== 'initializing' ? this.#state.fastIdeas.value : [];
                return {
                    queryKey: [
                        'events-widget',
                        'table',
                        symbolFilterValue,
                        source?.type ?? null,
                        source?.id ?? null,
                        fastIdeasValue.map((idea) => idea.idea.id),
                    ],
                    queryFn: async (_context, debugIdentifier?: string) => {
                        let stage: Stage;
                        if (symbolFilterValue) {
                            stage = SourceStage.allForKey('ticker');
                        } else if (source?.type === 'idea') {
                            const allStage = SourceStage.allForKey('ticker');
                            stage = ideaDTOToStage(allStage, source.dto);
                        } else if (!source) {
                            stage = SourceStage.allForKey('ticker');
                        } else {
                            stage = SourceStage.fromTickerUniverseId(source.id);
                        }

                        if (symbolFilterValue) {
                            stage = FilterStage.fromInputStage(stage, {
                                type: 'filter',
                                formula: `symbol == "${symbolFilterValue}"`,
                                imports: [{ ...symbolSlug.value, alias: 'symbol' }],
                            });
                        } else {
                            if (fastIdeasValue.length > 1) {
                                const stages = fastIdeasValue.map((idea) => ideaDTOToStage(stage, idea));

                                if (stages.length > 0) {
                                    stage = UnionStage.from(stages);
                                }
                            } else if (fastIdeasValue.length === 1) {
                                const idea = fastIdeasValue[0];
                                stage = ideaDTOToStage(stage, idea);
                            }
                        }

                        const formula = 'changeClose > 0 ? 1 : changeClose < 0 ? -1 : 0';
                        const imports = [{ ...changeCloseSlug.value, alias: 'changeClose' }];

                        stage = OutputStage.fromInputStage(stage, {}, [
                            { id: 'symbol', type: 'indicator', indicator: symbolSlug.value },
                            { id: 'changeDirection', type: 'formula', formula, imports },
                        ]);

                        return {
                            ...(await createTable({ leafStage: stage.build() }, undefined, debugIdentifier)),
                            fetchMode: undefined,
                        };
                    },
                    enabled: true,
                    staleTime: 0,
                    gcTime: 0,
                    refetchOnWindowFocus: false,
                };
            },
        );

        // Table replacement waits for a fresh membership image; date/type changes reuse known membership.
        const tableOptions = signal(queryOptions.peek());
        let tableKey: string | undefined;
        let sourceKey: string | undefined;
        this.disposableStack.defer(
            effect(() => {
                const options = queryOptions.value;
                const nextTableKey = hashKey(options.queryKey!);
                const query = eventsQuery(this.gotoDateSignal.value, this.eventTypes.value);
                const nextKey = hashKey([nextTableKey, query]);
                if (nextKey !== sourceKey) {
                    const source = {
                        sourceId: nextKey,
                        selectionKind: this.symbolFilter?.peek()
                            ? ('single-symbol' as const)
                            : ('multi-symbol' as const),
                        mode: query.anchor ? ('historical' as const) : ('live' as const),
                        query,
                        retention: this.symbolRetention?.peek().toMillis() ?? 0,
                    };
                    const status = this.eventsList.state.peek().source.status;
                    if (nextTableKey !== tableKey || status === 'awaiting-table-reset') {
                        this.eventsList.beginSource(source);
                    } else if (status === 'awaiting-symbols') {
                        this.eventsList.beginSource(source);
                        this.eventsList.membershipReset();
                    } else {
                        this.eventsList.activateSource(source, this.eventsList.state.peek().activeSymbols);
                    }
                    tableKey = nextTableKey;
                    sourceKey = nextKey;
                }
                tableOptions.value = options;
            }),
        );
        this.disposableStack.defer(
            effect(() => {
                this.eventsList.setRetention(this.symbolRetention?.value.toMillis() ?? 0);
            }),
        );
        this.#table.init({
            initBounds: { firstRow: 0, windowSize: 100000 },
            initThrottle: 1000,
            queryOptions: tableOptions,
            devtoolsName: tabId ? 'Events/table' : 'Events/standalone/table',
        });

        this.disposableStack.use(
            this.#table.updatesWithReset$.subscribe((updates) => {
                if (!updates) return;
                if (TRACE) {
                    this.#logger.trace('Events membership batch received', {
                        kind: updates === NEW_TABLE_RESET ? 'reset' : updates.type,
                        sourceStatus: this.eventsList.state.peek().source.status,
                        tableKey: this.#table.key,
                    });
                }

                if (updates === NEW_TABLE_RESET) {
                    this.eventsList.membershipReset();
                    this.#rowIdToSymbolMap.clear();
                    this.#unresolvedSymbolRowIds.clear();
                    this.#symbolChangeDirectionsMap.value = new Map();
                    return;
                }

                if (this.eventsList.state.peek().source.status === 'awaiting-table-reset') return;
                if (updates.type === 'quote' && this.eventsList.state.peek().source.status === 'awaiting-symbols') {
                    return;
                }

                if (updates.type === 'snapshot') {
                    this.#rowIdToSymbolMap.clear();
                    this.#unresolvedSymbolRowIds.clear();
                    const newSymbols = new Set<string>();
                    // Create a new Map to avoid modifying the one in the signal
                    const changeDirections = new Map<string, number>();

                    for (const update of updates) {
                        this.#rowIdToSymbolMap.set(update[RowIdField]!, update.symbol);
                        newSymbols.add(update.symbol);

                        // Get change direction directly from the changeDirection field
                        const changeDirection = Number(update['changeDirection']);
                        if (!isNaN(changeDirection)) {
                            changeDirections.set(update.symbol, changeDirection);
                        }
                    }

                    // Update the change directions signal
                    this.#symbolChangeDirectionsMap.value = changeDirections;
                    if (this.eventsList.state.peek().source.status === 'awaiting-symbols') {
                        this.eventsList.activateSymbols([...newSymbols]);
                    } else this.eventsList.publishSymbols([...newSymbols]);

                    this.#updateState({
                        type: 'SYMBOLS_SNAPSHOT_RECEIVED',
                        symbols: newSymbols,
                    });
                    return;
                } else if (updates.type === 'diff') {
                    const previousSymbols = new Set(this.eventsList.state.peek().activeSymbols);
                    const changeDirections = new Map(this.#symbolChangeDirectionsMap.peek());
                    for (const rowId of updates.deletedRowIds) {
                        this.#rowIdToSymbolMap.delete(rowId);
                        this.#unresolvedSymbolRowIds.delete(rowId);
                    }
                    for (const rowId of updates.orderedRowIds) {
                        if (!this.#rowIdToSymbolMap.has(rowId)) this.#unresolvedSymbolRowIds.add(rowId);
                    }
                    for (const rowId of updates.rowIdToRowIndex.keys()) {
                        if (!this.#rowIdToSymbolMap.has(rowId)) this.#unresolvedSymbolRowIds.add(rowId);
                    }
                    for (const update of updates) {
                        const rowId = update[RowIdField]!;
                        const symbol = update.symbol;
                        if (!symbol) {
                            if (!this.#rowIdToSymbolMap.has(rowId)) this.#unresolvedSymbolRowIds.add(rowId);
                            continue;
                        }
                        this.#rowIdToSymbolMap.set(rowId, symbol);
                        this.#unresolvedSymbolRowIds.delete(rowId);
                        const direction = Number(update.changeDirection);
                        if (!isNaN(direction)) changeDirections.set(symbol, direction);
                    }
                    // Only explicit deletions remove membership. Missing ordering entries are not removals.
                    // Keep unresolved IDs across batches until their values arrive or they are deleted.
                    const symbols = new Set(this.#rowIdToSymbolMap.values());
                    for (const symbol of changeDirections.keys()) {
                        if (!symbols.has(symbol)) changeDirections.delete(symbol);
                    }
                    this.#symbolChangeDirectionsMap.value = changeDirections;
                    if (this.#unresolvedSymbolRowIds.size > 0) {
                        if (TRACE) {
                            this.#logger.trace('Events membership awaiting complete symbol values', {
                                unresolvedRows: this.#unresolvedSymbolRowIds.size,
                                knownRows: this.#rowIdToSymbolMap.size,
                            });
                        }
                        return;
                    }
                    if (this.eventsList.state.peek().source.status === 'awaiting-symbols') {
                        this.eventsList.activateSymbols([...symbols]);
                        this.#updateState({ type: 'SYMBOLS_SNAPSHOT_RECEIVED', symbols });
                        return;
                    }
                    // Compare complete memberships, preserving delete/re-add and duplicate symbol rows.
                    const added = symbols.difference(previousSymbols);
                    const removed = previousSymbols.difference(symbols);
                    this.eventsList.updateSymbols([...added], [...removed]);
                    this.#updateState({ type: 'SYMBOLS_UPDATE_RECEIVED', added, removed });
                } else if (updates.type === 'quote') {
                    // Create a new Map from the current change directions
                    const changeDirections = new Map(this.#symbolChangeDirectionsMap.value);

                    // Handle quote updates
                    for (const update of updates) {
                        const symbol = update.symbol;
                        if (symbol) {
                            // Get change direction directly from the changeDirection field
                            const changeDirection = Number(update['changeDirection']);
                            if (!isNaN(changeDirection)) {
                                changeDirections.set(symbol, changeDirection);
                            }
                        }
                    }

                    // Update the change directions signal
                    this.#symbolChangeDirectionsMap.value = changeDirections;
                }
            }),
        );

        // Sync connection status for new header system
        this.disposableStack.defer(
            effect(() => {
                const status = this.#table.connectionStatus.value;
                const progress = this.#table.progress.value;
                const failureCount = this.#table.failureCount.value;
                this.#connectionStatus.value = {
                    status,
                    progress,
                    failureCount,
                };
            }),
        );
    }

    #logger: Logger;

    /**
     * Builds the EventBus for the new widget header system.
     * Combines widget-specific signals/events with slice-provided ones.
     */
    #buildEventBus(tabId?: string): EventsWidgetModel['eventBus'] {
        let merged = MergedEventBus.from(
            EventBusBuilder.create()
                // Events-specific signals
                .addSignal('widget::instrument', () =>
                    computed(() => {
                        const state = this.#stateSignal.value;
                        if (state.type === 'initializing') return '';
                        return state.symbolFilter.value ?? '';
                    }),
                )
                .addSignal('widget::allowedInstrumentTypes', () => this.#allowedInstrumentTypes)
                .addSignal('widget::symbolRetention', () =>
                    computed(() => {
                        const state = this.#stateSignal.value;
                        if (state.type === 'initializing') return Duration.ofSeconds(0);
                        return state.symbolRetention.value;
                    }),
                )
                .addSignal('widget::connectionStatus', () => this.#connectionStatus)
                .addSignal('widget::eventsGoToDate', () => this.#gotoDateSignal)
                .addSignal('widget::eventsEventTypeFilter', () => this.#eventTypes)
                .addSignal('widget::slowIdea', () => this.#slowIdeaDTO)
                .addSignal('widget::selectedFastIdeas', () =>
                    computed(() => {
                        const state = this.#stateSignal.value;
                        if (state.type === 'initializing') return [];
                        return state.fastIdeas.value.flatMap((idea) => {
                            const id = idea.idea.id;
                            return id !== undefined ? [{ type: 'idea' as const, id }] : [];
                        });
                    }),
                )
                .addSignal('widget::selectedSlowIdeas', () =>
                    computed(() => {
                        const source = this.#slowIdeaSourceSignal.value;
                        if (!source) return [];
                        return [{ type: source.type, id: source.id }];
                    }),
                )
                // Events-specific events
                .addEvent('widget::instrumentChange', (payload) => {
                    this.onChangeSymbolFilter(payload.instrument);
                })
                .addEvent('widget::updateSymbolRetention', (payload) => {
                    this.onChangeSymbolRetention(Duration.parse(payload.duration));
                })
                .addEvent('widget::onIdeasSelect', (payload) => {
                    const fastIdeas = payload.ideas.filter((idea) => !('universeId' in idea)) as (
                        | IdeaDTO<ScannerPlanDTO>
                        | IdeaDTO<SearchPlanDTO>
                    )[];
                    this.onSelectFastIdeas(fastIdeas);
                })
                .addEvent('widget::onIdeaDelete', () => {
                    // Events widget doesn't support idea deletion
                })
                .addEvent('widget::selectSlowIdea', (payload) => {
                    this.onSelectSlowIdeas(payload.ideas as Parameters<typeof this.onSelectSlowIdeas>[0]);
                })
                .addEvent('widget::connectionStatusRefetch', () => {
                    this.refetch();
                })
                .addEvent('widget::eventsGoToDateChange', (payload) => {
                    this.onGoToDate(payload.date);
                })
                .addEvent('widget::eventsEventTypeFilterChange', (payload) => {
                    this.onSelectEventTypes(payload.eventTypes);
                })
                .addSignal('widget::standalonePageUrl', () =>
                    computed(() => {
                        const state = this.#stateSignal.value;
                        const params = new URLSearchParams();

                        const symbol = state.type !== 'initializing' ? state.symbolFilter.value : null;
                        if (symbol) params.set('symbol', symbol);

                        const universe = this.#universeId.value;
                        if (universe) params.set('universe', universe);

                        const date = this.#gotoDateSignal.value;
                        if (date) params.set('date', date.toString());

                        const types = this.#eventTypes.value;
                        if (types.length > 0) params.set('types', types.join(','));

                        const fastIdeas = state.type !== 'initializing' ? state.fastIdeas.value : [];
                        const fastIdeaIds = fastIdeas.flatMap((idea) => (idea.idea.id ? [idea.idea.id] : []));
                        if (fastIdeaIds.length > 0) params.set('fastIdea', fastIdeaIds.join(','));

                        const retention = state.type !== 'initializing' ? state.symbolRetention.value : null;
                        if (retention && !retention.isZero()) params.set('retain', retention.toString());

                        if (this.#displayingCalendarSignal.value) {
                            params.set('calendar', 'true');
                        }

                        if (this.#splitPercentageSignal.value !== 50) {
                            params.set('split', String(this.#splitPercentageSignal.value));
                        }

                        const query = params.toString();
                        return `/events${query ? `?${query}` : ''}`;
                    }),
                )
                .build(),
        );

        if (tabId) {
            // Workspace mode: merge slice event buses (they provide widget::name, channel signals, etc.)
            merged = merged.merge(this.#channelSlice.eventBus).merge(this.#widgetTabSlice.eventBus);
        } else {
            // Standalone mode: stub workspace-specific signals since slices aren't initialized
            merged = merged.merge(
                EventBusBuilder.create()
                    .addSignal('widget::channel', () => signal<string | null>(null) as ReadonlySignal<string | null>)
                    .addSignal('widget::tabId', () => signal('standalone') as ReadonlySignal<string>)
                    .addSignal('widget::name', () => signal('') as ReadonlySignal<string>)
                    .addSignal('widget::isMaximized', () => signal(false) as ReadonlySignal<boolean>)
                    .addSignal(
                        'widget::container',
                        () => signal(null as unknown as ContainerViewModel) as ReadonlySignal<ContainerViewModel>,
                    )
                    .addSignal(
                        'widget::containerActiveTab',
                        () => signal(null as unknown as WidgetTabViewModel) as ReadonlySignal<WidgetTabViewModel>,
                    )
                    .addSignal('widget::tabsById', () => signal<ByIdState>({}) as ReadonlySignal<ByIdState>)
                    .addEvent('widget::channelChange', noop)
                    .addEvent('widget::resetChannelData', noop)
                    .addEvent('widget::toggleMaximized', noop)
                    .addEvent('widget::delete', noop)
                    .addEvent('widget::changeTab', noop)
                    .addEvent('widget::deleteTab', noop)
                    .addEvent('widget::changeTabName', noop)
                    .build(),
            );
        }

        return merged as EventsWidgetModel['eventBus'];
    }

    #rowIdToSymbolMap = new Map<number, string>();
    #unresolvedSymbolRowIds = new Set<number>();
    #symbolChangeDirectionsMap = signal<Map<string, number>>(new Map());

    get symbolChangeDirections(): ReadonlySignal<ReadonlyMap<string, number>> {
        return this.#symbolChangeDirectionsMap;
    }

    #eventTypes = signal<EventType[]>([]);

    get eventTypes(): ReadonlySignal<EventType[]> {
        return this.#eventTypes;
    }

    onGoToDate(date: LocalDate | null) {
        this.#adapter.persist({ gotoDate: date });
    }

    onSelectEventTypes(eventTypes: EventType[]): void {
        this.#adapter.persist({ eventTypes });
    }

    onSelectSlowIdeas(
        ideas: (
            | IdeaDTO<ScreenerPlanDTO>
            | IdeaDTO<WatchlistPlanDTO>
            | { universeId: string }
            | { type: string; id: string }
        )[],
    ): void {
        if (TRACE) {
            this.#logger.trace('onSelectSlowIdeas received ideas', {
                ideas: JSON.stringify(ideas),
                currentSource: this.#slowIdeaSourceSignal.value,
            });
        }

        const firstIdea = ideas[0];

        if (!firstIdea) {
            // Clear selection
            this.#slowIdeaSourceSignal.value = null;
            this.#adapter.persist({ universeId: null, slowIdeaId: null });
            return;
        }

        // Check for IdeaDTO (screener or watchlist) — has an 'idea' property
        if ('idea' in firstIdea) {
            const ideaDTO = firstIdea as IdeaDTO<ScreenerPlanDTO | WatchlistPlanDTO>;
            const ideaId = ideaDTO.idea.id ?? null;
            if (TRACE) {
                this.#logger.trace('Selected screener/watchlist slow idea', { ideaId });
            }
            // Set in-memory signal first so the adapter subscription can detect it's an idea
            this.#slowIdeaSourceSignal.value = ideaId ? { type: 'idea', id: ideaId, dto: ideaDTO } : null;
            // Store the idea ID in both universeId (legacy single-field paradigm) and slowIdeaId
            const currentState = this.#adapter.getState();
            if (ideaId !== currentState.universeId || ideaId !== currentState.slowIdeaId) {
                this.#adapter.persist({ universeId: ideaId, slowIdeaId: ideaId });
            }
            return;
        }

        let selectedUniverse: string | null = null;

        const ideaWithUniverseId = ideas.find((idea) => isUniverseIdFormat(idea));
        if (ideaWithUniverseId) {
            selectedUniverse = ideaWithUniverseId.universeId;
        } else {
            const ideaWithTypeUniverse = ideas.find((idea) => isTypeIdFormat(idea));
            if (ideaWithTypeUniverse) {
                selectedUniverse = ideaWithTypeUniverse.id;
            }
        }

        if (TRACE) {
            this.#logger.trace('Selected universe', {
                selectedUniverse,
                currentSource: this.#slowIdeaSourceSignal.value,
            });
        }

        const currentId = this.#slowIdeaSourceSignal.value?.id ?? null;
        if (currentId !== selectedUniverse) {
            this.#slowIdeaSourceSignal.value = selectedUniverse ? { type: 'universe', id: selectedUniverse } : null;
            this.#adapter.persist({ universeId: selectedUniverse, slowIdeaId: null });
        }
    }

    onSelectFastIdeas(ideas: (IdeaDTO<ScannerPlanDTO> | IdeaDTO<SearchPlanDTO>)[]): void {
        this.#updateState({ type: 'UPDATE_FAST_IDEAS', ideas });
        const ideaIds = ideas.map((idea) => idea.idea.id).filter((id): id is string => id !== undefined);
        this.#adapter.persist({ fastIdeasIds: ideaIds });
    }

    onSelectSymbol(symbol: string | null) {
        if (symbol !== null) {
            this.#adapter.onSelectSymbol(symbol);
        }
    }

    openChartForSymbol(symbol: string) {
        this.#adapter.openChartForSymbol(symbol);
    }

    openLensForSymbol(symbol: string) {
        this.#adapter.openLensForSymbol(symbol);
    }

    #resetState() {
        if (this.#state.type === 'initializing') {
            return;
        }

        this.#adapter.persist({ symbolFilter: null, gotoDate: null });

        this.#updateState({ type: 'RESET' });
    }

    #state: EventsWidgetModelState;
    #stateSignal = signal<EventsWidgetModelState>({ type: 'initializing' });
    #devtools: ReducerDevtoolsConnection<EventsWidgetModelState, EventsWidgetModelAction> | undefined;

    @reacts private set state(newState: EventsWidgetModelState) {
        this.#state = newState;
        this.#stateSignal.value = newState;
    }

    #updateState(action: EventsWidgetModelAction) {
        const prevState = this.#state;
        if (TRACE) {
            this.#logger.trace('Dispatching action', { type: action.type });
        }
        const newState = eventsWidgetModelReducer(this.#state, action);

        if (prevState !== newState) {
            if (TRACE) {
                this.#logger.trace('State updated', { action, prevState, state: this.#state });
            }

            this.state = newState;
            this.#devtools?.send(action, this.#state);

            // Keep slow idea source in sync with adapter — but only when no idea (screener/watchlist) is active
            if (
                this.#slowIdeaSourceSignal.value?.type !== 'idea' &&
                this.#adapter.getState().universeId !== (this.#slowIdeaSourceSignal.value?.id ?? null)
            ) {
                const adapterUniverseId = this.#adapter.getState().universeId;
                this.#slowIdeaSourceSignal.value = adapterUniverseId
                    ? { type: 'universe', id: adapterUniverseId }
                    : null;
            }

            if (newState.type === 'initializing') {
                this.#viewDetailsSignal.value = { type: 'loading' };
            } else if (newState.type === 'waiting-for-symbols') {
                this.#viewDetailsSignal.value = {
                    type: 'waiting-for-symbols',
                };
            } else {
                this.#viewDetailsSignal.value = {
                    type: 'ready',
                    instanceId: newState.instanceId,
                    symbols: newState.symbols,
                    symbolFilter: newState.symbolFilter,
                    fastIdeas: newState.fastIdeas,
                };
            }
        }
    }

    get symbolFilter(): ReadonlySignal<string | null> | null {
        if (this.#state.type !== 'initializing') {
            return this.#state.symbolFilter;
        }
        return null;
    }

    get symbolRetention(): ReadonlySignal<Duration> | null {
        if (this.#state.type !== 'initializing') {
            return this.#state.symbolRetention;
        }
        return null;
    }

    onChangeSymbolFilter(value: string) {
        const currentValue = this.#state.type !== 'initializing' ? this.#state.symbolFilter.value : null;
        if ((value === '' && currentValue === null) || value === currentValue) {
            return;
        }
        this.#adapter.persist({ symbolFilter: value === '' ? null : value });
    }

    onChangeSymbolRetention(duration: Duration) {
        this.#adapter.persist({ symbolRetention: duration.toString() });
    }

    get symbols(): ReadonlySignal<ReadonlySet<string>> | null {
        if (this.#state.type === 'ready') {
            return this.#state.symbols as ReadonlySignal<ReadonlySet<string>>;
        }
        return null;
    }

    get slowIdeaSource(): ReadonlySignal<SlowIdeaSource | null> {
        return this.#slowIdeaSourceSignal;
    }

    get gotoDateSignal() {
        return this.#gotoDateSignal;
    }

    #slowIdeaSourceSignal = signal<SlowIdeaSource | null>(null);
    // Convenience computeds for internal consumption
    #universeId = computed(() => {
        const s = this.#slowIdeaSourceSignal.value;
        return s?.type === 'universe' ? s.id : null;
    });
    #slowIdeaDTO = computed(() => {
        const s = this.#slowIdeaSourceSignal.value;
        return s?.type === 'idea' ? s.dto : null;
    });
    #gotoDateSignal = signal<LocalDate | null>(null);
    #displayingCalendarSignal = signal(false);
    #splitPercentageSignal = signal(50);

    get displayingCalendar(): boolean {
        return this.#displayingCalendarSignal.value;
    }

    set displayingCalendar(value: boolean) {
        this.#displayingCalendarSignal.value = value;
        this.#adapter.persist({ displayingCalendar: value });
    }

    get splitPercentage(): number {
        return this.#splitPercentageSignal.value;
    }

    set splitPercentage(value: number) {
        const clamped = Math.max(0, Math.min(100, value));
        this.#splitPercentageSignal.value = clamped;
        this.#adapter.persist({ splitPercentage: clamped });
    }

    #viewDetailsSignal = signal<EventsWidgetViewDetails>({ type: 'loading' });

    get viewDetails(): ReadonlySignal<EventsWidgetViewDetails> {
        return this.#viewDetailsSignal;
    }

    /**
     * Returns a synthetic EventsWidgetViewModel built from adapter state and internal signals.
     */
    get widget(): EventsWidgetViewModel {
        const adapterState = this.#adapter.getState();
        return {
            type: 'events',
            slowIdeaId: adapterState.slowIdeaId,
            fastIdeaIds: adapterState.fastIdeasIds,
            universeId: adapterState.universeId,
            symbolFilter: this.#state.type !== 'initializing' ? (this.#state.symbolFilter.value ?? null) : null,
            eventTypes: this.#eventTypes.value,
            gotoDate: this.#gotoDateSignal.value,
            symbolRetention:
                this.#state.type !== 'initializing'
                    ? this.#state.symbolRetention.value.toString()
                    : DEFAULT_SYMBOL_RETENTION.toString(),
            displayingCalendar: this.displayingCalendar,
            splitPercentage: this.splitPercentage,
            channelId: null,
        };
    }

    get connectionStatus(): ReadonlySignal<TableStatus> {
        return this.#table.connectionStatus;
    }

    get progress(): ReadonlySignal<TableConstructionProgress> {
        return this.#table.progress;
    }

    refetch(): void {
        this.#table.refetch();
    }
}
